import { useCallback, useEffect, useMemo, useState, type SetStateAction } from 'react';
import {
  Cancel01Icon,
  DashboardSquare03Icon,
  LicenseIcon,
  Ticket01Icon,
  UserGroupIcon,
  PackageReceiveIcon,
} from '@hugeicons/core-free-icons';
import type { IconSvgElement } from '@hugeicons/react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { HugeIcon } from '../components/ui/HugeIcon';
import {
  AuthenticationRequiredError,
  ConsoleAccessDeniedError,
  fetchConsoleState,
  fetchCustomerIdentityHealth,
  loginUrl,
  logoutUrl,
  type AppState,
  type ConsoleState,
  markEveryNotificationAsRead,
  markNotificationAsRead,
  type Notification,
  type CustomerIdentityHealth,
} from '../lib/api';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { UsersPage } from '../features/users/UsersPage';
import { LicensePage } from '../features/license/LicensePage';
import { TicketsPage } from '../features/tickets/TicketsPage';
import { PlatformPage } from '../features/platform/PlatformPage';
import { ConsoleSidebar } from '../components/layout/ConsoleSidebar';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '../components/ui/sidebar';
import { UpdatesPage } from '../features/updates/UpdatesPage';
import { NotificationCenter } from '../components/layout/NotificationCenter';
import { HeaderUserMenu } from '../components/layout/HeaderUserMenu';

export type View = 'dashboard' | 'users' | 'license' | 'updates' | 'tickets';

const navItems: Array<{ id: View; label: string; icon: IconSvgElement }> = [
  { id: 'dashboard', label: 'Overview', icon: DashboardSquare03Icon },
  { id: 'users', label: 'Users', icon: UserGroupIcon },
  { id: 'license', label: 'License', icon: LicenseIcon },
  { id: 'updates', label: 'Updates', icon: PackageReceiveIcon },
  { id: 'tickets', label: 'Tickets', icon: Ticket01Icon },
];

const viewPaths: Record<View, string> = {
  dashboard: '/',
  users: '/users',
  license: '/license',
  updates: '/updates',
  tickets: '/tickets',
};

function viewFromLocation() {
  if (typeof window === 'undefined') return 'dashboard';

  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const matchedView = (Object.entries(viewPaths) as Array<[View, string]>).find(
    ([, path]) => path === normalizedPath
  );

  return matchedView?.[0] ?? 'dashboard';
}

export function App() {
  const [state, setState] = useState<AppState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [view, setView] = useState<View>(() => viewFromLocation());
  const [identityHealth, setIdentityHealth] = useState<CustomerIdentityHealth | null>(null);
  const activeViewLabel = navItems.find((item) => item.id === view)?.label ?? 'Overview';

  const navigateToView = useCallback((nextView: View) => {
    setView(nextView);

    if (typeof window === 'undefined') return;

    const nextPath = viewPaths[nextView];
    if (window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
  }, []);

  useEffect(() => {
    function syncViewFromHistory() {
      setView(viewFromLocation());
    }

    window.addEventListener('popstate', syncViewFromHistory);

    return () => {
      window.removeEventListener('popstate', syncViewFromHistory);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetchConsoleState()
      .then((data) => {
        if (isMounted) {
          setState(data);
          setNeedsLogin(false);
          setAccessDenied(null);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          if (err instanceof AuthenticationRequiredError) {
            setNeedsLogin(true);
            setAccessDenied(null);
            setError(null);
            return;
          }

          if (err instanceof ConsoleAccessDeniedError) {
            setNeedsLogin(false);
            setAccessDenied(err.message);
            setError(null);
            return;
          }

          setError(err instanceof Error ? err.message : 'Unable to load console state.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const customerState = state?.mode === 'customer' ? state : null;
  useEffect(() => {
    if (!customerState || view !== 'dashboard') {
      setIdentityHealth(null);
      return;
    }
    let active = true;
    fetchCustomerIdentityHealth()
      .then((health) => { if (active) setIdentityHealth(health); })
      .catch(() => { if (active) setIdentityHealth(null); });
    return () => { active = false; };
  }, [customerState?.organization.id, view]);
  const updateCustomerState = useCallback((value: SetStateAction<ConsoleState | null>) => {
    setState((current) => {
      const currentCustomer = current?.mode === 'customer' ? current : null;
      const nextState = typeof value === 'function' ? value(currentCustomer) : value;
      return nextState;
    });
  }, []);
  const content = useMemo(() => {
    if (!customerState) return null;

    switch (view) {
      case 'users':
        return (
          <UsersPage
            license={customerState.license}
            members={customerState.members}
            onStateChange={updateCustomerState}
            userRequests={customerState.userRequests}
          />
        );
      case 'license':
        return (
          <LicensePage
            license={customerState.license}
            organization={customerState.organization}
            releases={customerState.releases}
            usedSeats={customerState.members.filter((member) => member.status !== 'revoked').length}
          />
        );
      case 'updates':
        return <UpdatesPage state={customerState} />;
      case 'tickets':
        return <TicketsPage onStateChange={updateCustomerState} tickets={customerState.tickets} />;
      case 'dashboard':
      default:
        return <DashboardPage identityHealth={identityHealth} state={customerState} setView={navigateToView} />;
    }
  }, [customerState, identityHealth, navigateToView, updateCustomerState, view]);

  const openNotification = useCallback((notification: Notification) => {
    if (!customerState) return;
    if (!notification.readAt) {
      const readAt = new Date().toISOString();
      updateCustomerState((current) => current ? { ...current, notifications: current.notifications.map((item) => item.id === notification.id ? { ...item, readAt } : item) } : current);
      void markNotificationAsRead(notification.id);
    }
    const next = (Object.entries(viewPaths) as Array<[View, string]>).find(([, path]) => notification.actionPath?.startsWith(path) && path !== '/')?.[0];
    if (next) navigateToView(next);
  }, [customerState, navigateToView, updateCustomerState]);

  const readAllNotifications = useCallback(() => {
    const readAt = new Date().toISOString();
    updateCustomerState((current) => current ? { ...current, notifications: current.notifications.map((item) => ({ ...item, readAt })) } : current);
    void markEveryNotificationAsRead();
  }, [updateCustomerState]);

  if (needsLogin || error) {
    return <LoginScreen error={error} />;
  }

  if (accessDenied) {
    return <AccessDeniedScreen message={accessDenied} />;
  }

  if (!state) {
    return (
      <main className="min-h-screen bg-[var(--color-page)] px-5 py-8 text-slate-900">
        <div className="mx-auto flex min-h-[70vh] max-w-[1248px] items-center justify-center">
          <div className="glass-surface rounded-[24px] px-8 py-6 text-sm text-slate-600">
            Loading organization console
          </div>
        </div>
      </main>
    );
  }

  if (state.mode === 'platform') {
    return <PlatformPage onStateChange={setState} state={state} />;
  }

  return (
    <SidebarProvider className="bg-[var(--color-page)] text-slate-900">
      <ConsoleSidebar
        activeView={view}
        label={state.organization.name}
        navItems={navItems}
        onNavigate={navigateToView}
        licensePeriod={{
          expiryDate: state.license?.expiryDate,
          startDate: state.license?.startDate,
        }}
        seatUsage={{
          limit: state.license?.seatLimit ?? 0,
          used: state.members.filter((member) => member.status !== 'revoked').length,
        }}
      />
      <SidebarInset className="w-auto! min-w-0 bg-background md:m-0 md:rounded-none md:shadow-none">
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-3 border-b bg-card px-3 sm:px-4">
            <div className="flex min-w-0 items-center gap-3">
              <SidebarTrigger className="shrink-0" />
              <h1 className="truncate text-sm font-semibold text-foreground">{activeViewLabel}</h1>
            </div>
            <div className="flex items-center gap-2">
              <NotificationCenter notifications={state.notifications} onOpenNotification={openNotification} onReadAll={readAllNotifications} />
              <HeaderUserMenu currentUser={state.currentUser} logoutHref={logoutUrl()} />
            </div>
        </header>
        <div className="mx-auto w-full max-w-[1600px] min-w-0 flex-1 p-3 sm:p-4 lg:p-6">{content}</div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function AccessDeniedScreen({ message }: { message: string }) {
  return (
    <main className="relative min-h-screen bg-[var(--color-page)] px-5 py-8 text-slate-900">
      <div className="mx-auto flex min-h-[82vh] max-w-[1248px] items-center justify-center">
        <section className="glass-surface w-full max-w-[440px] rounded-[24px] px-7 py-8 text-center shadow-[0_24px_72px_-48px_rgba(15,23,42,0.45)]">
          <img alt="myPDF" className="mx-auto mb-8 h-11 w-auto" src="/brand/mypdf-logo.svg" />
          <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-[18px] bg-red-50 text-red-600">
            <HugeIcon icon={Cancel01Icon} size={24} />
          </div>
          <h1 className="text-[26px] font-semibold leading-8 text-slate-900">
            Console access unavailable
          </h1>
          <p className="mt-4 text-sm font-medium leading-6 text-[#63738b]">
            {message}
          </p>
          <p className="mt-3 text-xs font-medium leading-5 text-[#63738b]">
            Members should use their assigned myPDF tools domain.
          </p>
          <a
            className="mt-8 inline-flex h-11 w-full items-center justify-center rounded-[18px] bg-slate-900 px-4 text-sm font-medium leading-6 text-white transition hover:bg-slate-800"
            href={logoutUrl()}
          >
            Sign out
          </a>
        </section>
      </div>
    </main>
  );
}

function LoginScreen({ error }: { error: string | null }) {
  return (
    <main className="relative min-h-screen bg-[var(--color-page)] px-5 py-8 text-slate-900">
      {error ? (
        <div className="fixed left-1/2 top-5 z-50 w-[calc(100%-40px)] max-w-[520px] -translate-x-1/2 md:top-8">
          <Alert>
            <HugeIcon icon={Cancel01Icon} size={16} />
            <div>
              <AlertTitle>Unable to sign in</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </div>
          </Alert>
        </div>
      ) : null}

      <div className="mx-auto flex min-h-[82vh] max-w-[1248px] items-center justify-center">
        <section className="w-full max-w-[360px]">
          <form
            action={loginUrl()}
            className="flex flex-col gap-8"
            method="get"
          >
            <div className="flex flex-col items-center gap-7 text-center">
              <img alt="myPDF" className="mb-2 h-11 w-auto" src="/brand/mypdf-logo.svg" />
              <div>
                <p className="text-sm font-medium leading-6 text-[#63738b]">
                  Sign in to the organization connected to your email.
                </p>
              </div>
            </div>

            <Button
              className="h-11 w-full rounded-[18px] bg-slate-900 px-4 text-white hover:bg-slate-800"
              type="submit"
            >
              Login to your Organization
            </Button>
          </form>
          <p className="mt-8 text-center text-xs font-medium leading-5 text-[#63738b]">
            Use the account invited to your myPDF organization.
          </p>
        </section>
      </div>
    </main>
  );
}
